import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Award, Wallet, Headphones } from 'lucide-react';
import { whyChooseUs } from '../data/mockData';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const WhyChooseUsSection = ({ onOpenContactForm }) => {
  const section = useScrollAnimation({ threshold: 0.15 });
  const icons = [Award, Wallet, Headphones];

  return (
    <section data-testid="why-choose-section" ref={section.ref} className="py-24 md:py-32 bg-[#030308] relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-stretch">
          {/* Content */}
          <div className={`lg:w-[55%] w-full order-2 lg:order-1 scroll-slide-left ${section.isVisible ? 'scroll-visible' : ''}`}>
            <span className="text-xs tracking-[0.2em] uppercase text-amber-500 mb-4 block font-medium">Why Trademark My LLC</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-black tracking-tighter text-white leading-[0.95] mb-6 uppercase" style={{ fontFamily: "'Outfit', sans-serif" }}>
              We Don't Just File.<br /><span className="text-gradient-primary">We Fight.</span>
            </h2>
            <p className="text-base text-slate-300 leading-relaxed mb-10">
              Cheap filing services submit your application and disappear. We assign a licensed trademark attorney to your case, handle every complication, and don't stop until your brand is protected.
            </p>

            <div className="space-y-6 mb-10">
              {whyChooseUs.map((item, index) => {
                const Icon = icons[index] || Award;
                return (
                  <div key={item.id} data-testid={`why-choose-card-${item.id}`} className="flex gap-5 group icon-shift cursor-default">
                    <div className="icon-container w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 transition-colors">
                      <Icon className="w-6 h-6 text-indigo-400 transition-colors" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-white mb-1 group-hover:text-amber-400 transition-colors" style={{ fontFamily: "'Outfit', sans-serif" }}>
                        {item.title}
                      </h4>
                      <p className="text-sm text-slate-400 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <Button
              data-testid="why-choose-cta"
              onClick={onOpenContactForm}
              className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold px-8 py-5 text-sm rounded-md uppercase tracking-wider btn-shine shadow-lg shadow-amber-500/20"
            >
              Talk to an Attorney
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>

          {/* Stats / Social proof */}
          <div className={`lg:w-[45%] w-full order-1 lg:order-2 scroll-slide-right scroll-delay-2 ${section.isVisible ? 'scroll-visible' : ''}`}>
            <div className="grid grid-cols-2 gap-4 h-full">
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6 flex flex-col justify-center items-center text-center card-hover-premium">
                <span className="text-5xl font-black text-gradient-amber" style={{ fontFamily: "'Outfit', sans-serif" }}>98%</span>
                <p className="text-sm text-slate-400 mt-2">Application approval rate</p>
              </div>
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6 flex flex-col justify-center items-center text-center card-hover-premium">
                <span className="text-5xl font-black text-gradient-primary" style={{ fontFamily: "'Outfit', sans-serif" }}>24h</span>
                <p className="text-sm text-slate-400 mt-2">Average filing turnaround</p>
              </div>
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6 flex flex-col justify-center items-center text-center card-hover-premium">
                <span className="text-5xl font-black text-gradient-primary" style={{ fontFamily: "'Outfit', sans-serif" }}>5K+</span>
                <p className="text-sm text-slate-400 mt-2">Trademarks filed</p>
              </div>
              <div className="bg-white/[0.02] border border-white/5 rounded-xl p-6 flex flex-col justify-center items-center text-center card-hover-premium">
                <span className="text-5xl font-black text-gradient-amber" style={{ fontFamily: "'Outfit', sans-serif" }}>$45</span>
                <p className="text-sm text-slate-400 mt-2">Starting price</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
